import React, { useState, useEffect, useRef } from 'react';

export default function FloatingVoiceMemo({ lead, currentUser, onSave, onClose }) {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl]   = useState(null);
  const [duration, setDuration]   = useState(0);
  const [error, setError]         = useState(null);
  const [pos, setPos] = useState({
    x: Math.max(60, window.innerWidth / 2 - 180),
    y: Math.max(60, window.innerHeight / 2 - 220),
  });

  const mediaRecorder = useRef(null);
  const chunks        = useRef([]);
  const timerRef      = useRef(null);
  const dragging      = useRef(false);
  const dragOff       = useRef({ x: 0, y: 0 });

  useEffect(() => {
    function onMove(e) {
      if (!dragging.current) return;
      setPos({ x: e.clientX - dragOff.current.x, y: e.clientY - dragOff.current.y });
    }
    function onUp() { dragging.current = false; }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  function startDrag(e) {
    if (e.target.closest('button, audio')) return;
    dragging.current = true;
    dragOff.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }

  async function startRecording() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorder.current = mr;
      chunks.current = [];

      mr.ondataavailable = e => chunks.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        const url  = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        stream.getTracks().forEach(t => t.stop());
      };

      mr.start();
      setRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch {
      setError('Mikrofon nicht verfügbar');
    }
  }

  function stopRecording() {
    if (mediaRecorder.current && recording) {
      mediaRecorder.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  }

  function handleSave() {
    if (!audioBlob) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const now = new Date();
      onSave({
        id: Date.now(),
        type: 'voice',
        month: now.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
        title: `Voice-Memo (${fmt(duration)})`,
        by: currentUser?.name || 'Nutzer',
        date: now.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        time: now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        ts: now.getTime(),
        audioData: reader.result,
        duration,
      });
    };
    reader.readAsDataURL(audioBlob);
  }

  function reset() {
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setDuration(0);
  }

  const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const btnBg = recording ? '#ef4444' : (audioBlob ? '#16a34a' : '#f0f5f0');

  return (
    <div
      style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: 10000, width: 320,
        background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(28px)',
        WebkitBackdropFilter: 'blur(28px)', border: '1.5px solid rgba(255,255,255,0.6)',
        borderRadius: 20, boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
        userSelect: 'none', animation: 'glModalIn .22s var(--motion-ease) both' }}
      onMouseDown={startDrag}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px 12px',
        borderBottom: '1px solid rgba(0,0,0,0.06)', cursor: 'grab' }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(239,68,68,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
            <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
            <path d="M19 10v2a7 7 0 01-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#22302a' }}>Voice-Memo</div>
          <div style={{ fontSize: 11, color: '#66796c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lead.name}
          </div>
        </div>
        <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: '#f0f5f0',
          border: 'none', cursor: 'pointer', color: '#66796c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ padding: '24px 18px 20px', textAlign: 'center' }}>
        {error && (
          <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12, padding: '8px 12px',
            background: 'rgba(239,68,68,0.08)', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)' }}>
            {error}
          </div>
        )}

        {/* Big record button */}
        <div style={{ position: 'relative', width: 88, height: 88, margin: '0 auto 16px' }}>
          {recording && (
            <>
              <div style={{ position: 'absolute', inset: -12, borderRadius: '50%', background: 'rgba(239,68,68,0.12)',
                animation: 'glPulseGlow 1.2s ease-in-out infinite' }} />
              <div style={{ position: 'absolute', inset: -6, borderRadius: '50%', background: 'rgba(239,68,68,0.08)',
                animation: 'glPulseGlow 1.2s ease-in-out infinite .3s' }} />
            </>
          )}
          <div
            onClick={recording ? stopRecording : (!audioBlob ? startRecording : undefined)}
            style={{ width: 88, height: 88, borderRadius: '50%', background: btnBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: recording || !audioBlob ? 'pointer' : 'default',
              transition: 'all .2s var(--motion-spring)',
              boxShadow: recording ? '0 0 0 6px rgba(239,68,68,0.2), 0 8px 32px rgba(239,68,68,0.25)' : '0 4px 16px rgba(0,0,0,0.12)' }}
          >
            {recording ? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="1.5">
                <rect x="6" y="6" width="12" height="12" rx="3"/>
              </svg>
            ) : audioBlob ? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="#16a34a" stroke="none">
                <polyline points="20 6 9 17 4 12" strokeWidth="3" stroke="white" fill="none"/>
              </svg>
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#66796c" strokeWidth="2">
                <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
                <path d="M19 10v2a7 7 0 01-14 0v-2"/>
              </svg>
            )}
          </div>
        </div>

        <div style={{ fontSize: 30, fontWeight: 800, color: recording ? '#ef4444' : '#22302a',
          fontFamily: 'var(--mono)', letterSpacing: '0.04em', lineHeight: 1, marginBottom: 6,
          transition: 'color .2s' }}>
          {fmt(duration)}
        </div>
        <div style={{ fontSize: 12, color: '#66796c', marginBottom: 20, minHeight: 16 }}>
          {recording ? 'Aufnahme läuft…' : audioBlob ? 'Bereit zum Speichern' : 'Klicken zum Aufnehmen'}
        </div>

        {/* Playback */}
        {audioUrl && !recording && (
          <audio controls src={audioUrl}
            style={{ width: '100%', height: 36, marginBottom: 12, borderRadius: 8, outline: 'none' }} />
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          {audioBlob && !recording && (
            <button onClick={reset}
              style={{ flex: 1, height: 38, borderRadius: 10, background: '#f0f5f0', border: '1px solid #e2ebe3',
                color: '#66796c', fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'background .1s' }}>
              Neu aufnehmen
            </button>
          )}
          <button onClick={handleSave} disabled={!audioBlob || recording}
            style={{ flex: 2, height: 38, borderRadius: 10, border: 'none',
              background: audioBlob && !recording ? 'var(--accent)' : '#e2ebe3',
              color: audioBlob && !recording ? 'white' : '#93a89a',
              fontSize: 13, fontWeight: 700, cursor: audioBlob && !recording ? 'pointer' : 'default',
              transition: 'all .15s' }}>
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
